import { sleepNightKey } from "../recency.js";
import { numericReading } from "./readings.js";
import { occurredAtMs } from "./signal-like.js";
/**
 * Day-bucketed Signal series and window statistics. Day boundaries follow the
 * user's IANA timezone when given; without one they fall back to UTC. A run at
 * 11pm local belongs to that local day, not to the next UTC day.
 */
const DAY_MS = 86_400_000;
/*
 * Constructing an Intl.DateTimeFormat costs about forty microseconds, and a
 * day-bucketed read makes tens of thousands of day keys, so the formatter is
 * built once per zone and kept. A zone the runtime rejects is remembered as a
 * miss, so a bad value is not re-thrown on every row.
 */
const DAY_FORMATTERS = new Map();
const MAX_DAY_FORMATTERS = 200;
function dayFormatter(timezone) {
    const cached = DAY_FORMATTERS.get(timezone);
    if (cached !== undefined)
        return cached;
    let formatter = null;
    try {
        formatter = new Intl.DateTimeFormat("en-CA", {
            timeZone: timezone,
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        });
    }
    catch {
        formatter = null;
    }
    if (DAY_FORMATTERS.size >= MAX_DAY_FORMATTERS)
        DAY_FORMATTERS.clear();
    DAY_FORMATTERS.set(timezone, formatter);
    return formatter;
}
export function dayKey(ms, timezone) {
    if (timezone) {
        const formatter = dayFormatter(timezone);
        if (formatter)
            return formatter.format(new Date(ms));
    }
    return new Date(ms).toISOString().slice(0, 10);
}
/** The day a reading belongs to. */
export function dayKeyForKind(kind, ms, timezone) {
    return DAY_KEY_POLICY[kind]?.keyOf(ms, timezone) ?? dayKey(ms, timezone);
}
/** How a kind is dated, in one place. */
const DAY_KEY_POLICY = {
    "wearable.sleep": {
        keyOf: (ms, timezone) => sleepNightKey(new Date(ms), timezone ?? "UTC"),
        windowShift: 1,
    },
};
/** The last `n` day keys ending `offset` days before today, oldest first. */
export function lastNDayKeys(now, n, offset = 0, timezone) {
    const out = [];
    for (let i = n - 1 + offset; i >= offset; i--) {
        // Subtracting whole days from the instant is DST-safe enough for day keys:
        // the formatted key only shifts when the local date genuinely changes.
        out.push(dayKey(now.getTime() - i * DAY_MS, timezone));
    }
    return out;
}
/** The same window, keyed the way `bucketLatestByDay` keys this kind. */
export function lastNDayKeysForKind(kind, now, n, offset = 0, timezone) {
    return lastNDayKeys(now, n, offset + windowShiftForKind(kind), timezone);
}
/** How far a window of `kind`'s day keys steps back, so it ends on a real one. */
export function windowShiftForKind(kind) {
    const kinds = typeof kind === "string" ? [kind] : kind;
    return Math.max(0, ...kinds.map((k) => DAY_KEY_POLICY[k]?.windowShift ?? 0));
}
/**
 * Latest reading per day for one kind. `pick` extracts the value from the
 * payload; return null to skip a Signal.
 */
export function bucketLatestByDay(signals, kind, pick, timezone) {
    const out = new Map();
    for (const s of signals) {
        if (s.kind !== kind)
            continue;
        const v = pick(s.payload);
        if (v === null)
            continue;
        const ts = occurredAtMs(s);
        const key = dayKeyForKind(kind, ts, timezone);
        const existing = out.get(key);
        if (!existing || existing.ts < ts)
            out.set(key, { value: v, ts });
    }
    return out;
}
/**
 * One value per day across a trailing window, oldest first, gaps filled with
 * 0. Values pass through the kind's zero policy via `numericReading`.
 */
export function seriesOverWindow(signals, opts) {
    return { values: seriesDays(signals, opts).map((v) => v ?? 0) };
}
/** The same window, with a day nobody reported left as null. */
export function seriesDaysOverWindow(signals, opts) {
    return { values: seriesDays(signals, opts) };
}
function seriesDays(signals, opts) {
    const kinds = typeof opts.kind === "string" ? new Set([opts.kind]) : new Set(opts.kind);
    const reducer = opts.reducer ?? "latest";
    const keys = lastNDayKeys(opts.now, opts.windowDays, opts.offsetDays ?? 0, opts.timezone);
    const index = new Map(keys.map((k, i) => [k, i]));
    const buckets = keys.map(() => null);
    for (const s of signals) {
        if (!kinds.has(s.kind))
            continue;
        const v = numericReading(s.payload, s.kind, opts.field);
        if (v === null)
            continue;
        const ts = occurredAtMs(s);
        const i = index.get(dayKeyForKind(s.kind, ts, opts.timezone));
        if (i === undefined)
            continue;
        const current = buckets[i] ?? null;
        if (current === null) {
            buckets[i] = { value: v, ts };
        }
        else if (reducer === "sum") {
            buckets[i] = { value: current.value + v, ts: Math.max(current.ts, ts) };
        }
        else if (reducer === "max") {
            buckets[i] = current.value >= v ? current : { value: v, ts };
        }
        else {
            buckets[i] = current.ts >= ts ? current : { value: v, ts };
        }
    }
    return buckets.map((b) => (b ? Math.round(b.value * 100) / 100 : null));
}
/** First (most recent, callers pass newest-first) Signal of a kind, or null. */
export function lastSignal(signals, kind) {
    for (const s of signals) {
        if (s.kind === kind)
            return s;
    }
    return null;
}
/** Policy-aware mean of one numeric field across all Signals of a kind. */
export function averageField(signals, kind, field) {
    const values = [];
    for (const s of signals) {
        if (s.kind !== kind)
            continue;
        const v = numericReading(s.payload, kind, field);
        if (v !== null)
            values.push(v);
    }
    if (values.length === 0)
        return null;
    return values.reduce((a, b) => a + b, 0) / values.length;
}
/**
 * The mean of one value per complete day, newest day excluded.
 *
 * `averageField` means every sample in the window, which for a kind that lands
 * as an overnight reading plus same-day spot checks weights a busy day above a
 * quiet one and folds in a partial today. Scores already read HRV this way; a
 * reading rendered beside a score has to agree with it, or the member sees two
 * numbers for one measurement.
 */
export function latestPerCompleteDay(signals, kind, field, now, timezone, days = 7, offset = 1) {
    const byDay = bucketLatestByDay(signals, kind, (payload) => numericReading(payload, kind, field), timezone);
    return lastNDayKeys(now, days, offset, timezone)
        .map((k) => byDay.get(k)?.value)
        .filter((v) => typeof v === "number");
}
/**
 * The mean of one value per complete day, newest day excluded.
 *
 * `averageField` means every sample in the window, which for a kind that lands
 * as an overnight reading plus same-day spot checks weights a busy day above a
 * quiet one and folds in a partial today. Scores already read HRV this way; a
 * reading rendered beside a score has to agree with it.
 */
export function avgLatestPerCompleteDay(signals, kind, field, now, timezone, days = 7, offset = 1) {
    const vals = latestPerCompleteDay(signals, kind, field, now, timezone, days, offset);
    if (vals.length === 0)
        return null;
    return vals.reduce((a, b) => a + b, 0) / vals.length;
}
export function median(values) {
    if (values.length === 0)
        return null;
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 0
        ? ((sorted[mid - 1] ?? 0) + (sorted[mid] ?? 0)) / 2
        : (sorted[mid] ?? 0);
}
//# sourceMappingURL=series.js.map